import React, { CSSProperties, useMemo } from 'react';
import { FeedbackFormElement, FeedbackFormElementTypeEnum } from '@src/types';
import { feedbackSingleFormHasAnswerError, GENDER_MAX_ANSWER_LIMIT } from '@src/utils/validation';

// Required-answer copy shared by every survey form (FeedbackForm and both FeedbackFormScroll variants).
export const selectErrorMessage = 'Please select an answer to continue.';
export const enterErrorMessage = 'Please enter an answer to continue.';
export const multiScaleErrorMessage = 'Please select an answer for each question.';
export const lengthErrorMessage = `Please answer with ${GENDER_MAX_ANSWER_LIMIT} characters or fewer.`;

// Why an incomplete question cannot move on. Only meaningful once feedbackSingleFormHasAnswerError says it is incomplete.
export const requiredAnswerMessage = (element: FeedbackFormElement): string => {
  switch (element.type) {
    case FeedbackFormElementTypeEnum.radio:
    case FeedbackFormElementTypeEnum.radioAssessable:
    case FeedbackFormElementTypeEnum.checkbox:
    case FeedbackFormElementTypeEnum.checkboxAssessable:
      if (!element.answer?.value?.length) return selectErrorMessage;
      break;
    case FeedbackFormElementTypeEnum.multiScale:
    case FeedbackFormElementTypeEnum.multiScaleAssessable:
      if (Object.keys(element.answer ?? {}).length < (element.statements?.length ?? 0)) return multiScaleErrorMessage;
      break;
    default:
      if (!element.answer?.value) return enterErrorMessage;
      if (element.type === FeedbackFormElementTypeEnum.text && element.lengthLimit && element.answer.value.length > GENDER_MAX_ANSWER_LIMIT) {
        return lengthErrorMessage;
      }
  }
  // Only the "Please explain your Answer" box is left.
  return enterErrorMessage;
};

// Next/Submit stay clickable but fade until the current question is answered.
export const useCurrentQuestionUnanswered = (elements: FeedbackFormElement[], currentIndex: number) => useMemo(
  () => !!elements[currentIndex] && feedbackSingleFormHasAnswerError(elements[currentIndex]),
  [elements, currentIndex]
);

const visuallyHidden: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

type AnnouncerProps = {
  element?: FeedbackFormElement;
  show: boolean;
};

// The visible error text is not a live region, so screen readers would stay silent after a faded Next/Submit click.
export function RequiredAnswerAnnouncer({ element, show }: AnnouncerProps) {
  return (
    <div role="status" aria-live="polite" style={visuallyHidden}>
      {show && element ? requiredAnswerMessage(element) : ''}
    </div>
  );
}
